import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Checkbox, Dropdown, Input, Menu, Table } from "antd";
import { MoreOutlined } from "@ant-design/icons";
import RoamingItemsFilterColumns from "./RoamingItemsFilterColumns";
import RoamingItemListEdit from "./RoamingItemListEdit/RoamingItemListEdit";
import AddToStock from "./AddToStock/AddToStock";
import useDebounce from "hooks/useDebounce";
import { EditableRow, GarmentItem, IFilterValue, RoamingItemsTableProps } from "domain/RoamingItems";

const RoamingItemsTable: React.FC<RoamingItemsTableProps> = ({
  loading,
  pagination,
  setPagination,
  data,
  setFilters,
  onAddItem,
}) => {
  const [tableData, setTableData] = useState<GarmentItem[]>(data);
  const [searchValues, setSearchValues] = useState<IFilterValue>({});
  const [editableRows, setEditableRows] = useState<Record<number, EditableRow>>({});
  const [openForm, setOpenForm] = useState({ editForm: false, addToStockForm: false })
  const [selectedRow, setSelectedRow] = useState<GarmentItem | null>(null);

  const debouncedSearchValue = useDebounce(searchValues, 900);
  const navigate = useNavigate();

  useEffect(() => {
    setTableData(data);
  }, [data]);

  const handleFilterChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>, dataIndex: string) => {
      const value = e.target.value;
      setSearchValues((prev) => ({ ...prev, [dataIndex]: value }));
    },
    []
  );

  useEffect(() => {
    const cleanedFilters: IFilterValue = Object.fromEntries(
      Object.entries(debouncedSearchValue)
        .filter(([_, value]) => value !== "" && value != null)
        .map(([key, value]) => [key, String(value)])
    );

    setPagination({ ...pagination, current: 1 });
    setFilters?.(cleanedFilters);
  }, [debouncedSearchValue]);

  const handleBulkUpdate = useCallback(() => {
    const roamingItems = Object.entries(editableRows).map(([garmentId, values]) => ({
      garment_id: Number(garmentId),
      price: Number(values.Price),
      sell_price: Number(values.SellPrice),
      is_deleted: values.is_deleted ?? true,
    }));

    onAddItem({ roamingItems });
  }, [editableRows, onAddItem]);

  const handlePageChange = (page: number, pageSize?: number) => {
    setPagination({
      ...pagination,
      current: page,
      pageSize: pageSize || pagination.pageSize,
    });
  };

  const actionColumn = {
    title: "",
    key: "actions",
    fixed: "right" as const,
    render: (_: unknown, record: GarmentItem) => {
      const menu = (
        <Menu
          items={[
            {
              key: "edit",
              label: "Edit",
              onClick: () => {
                setSelectedRow(record)
                setOpenForm(prev => ({ ...prev, editForm: true }));
              },
            },
            {
              key: "add-stock",
              label: "Add to stock",
              onClick: () => {
                setSelectedRow(record)
                setOpenForm(prev => ({ ...prev, addToStockForm: true }));
              },
            },
            {
              key: "deactivate",
              label: "Deactivate",
              onClick: () => {
                console.log("Deactivate", record);
              },
            },
            {
              key: "breakdown",
              label: "Breakdown by Customers",
              onClick: () => {
                navigate(`/roaming-items/list/client/${record?.GarmentId}`);
              },
            },
          ]}
        />
      );

      return (
        <Dropdown overlay={menu} trigger={["click"]}>
          <MoreOutlined style={{ fontSize: 18, cursor: "pointer" }} />
        </Dropdown>
      );
    },
  };

  const renderEditableCell = (field: "Price" | "SellPrice") =>
    (text: string | number, record: GarmentItem) =>
      editableRows[record.GarmentId] ? (
        <Input
          value={editableRows[record.GarmentId][field]}
          onChange={(e) =>
            setEditableRows((prev) => ({
              ...prev,
              [record.GarmentId]: {
                ...prev[record.GarmentId],
                [field]: e.target.value,
              },
            }))
          }
          onPressEnter={handleBulkUpdate}
          onClick={(e) => e.stopPropagation()}
        />
      ) : (
        text
      );

  const columns = [
    {
      title: "",
      dataIndex: "checkbox",
      key: "checkbox",
      render: (_: unknown, record: GarmentItem) => (
        <Checkbox
          checked={!!editableRows[record.GarmentId]}
          onChange={(e) => {
            setEditableRows((prev) => {
              const copy = { ...prev };
              if (e.target.checked) {
                copy[record.GarmentId] = {
                  Price: record.Price,
                  SellPrice: record.SellPrice,
                  is_deleted: true
                };
              } else {
                delete copy[record.GarmentId];
              }
              return copy;
            });
          }}
          onClick={(e) => e.stopPropagation()}
        />
      )
    },
    { title: 'Item', dataIndex: 'Item', key: 'Item' },
    { title: 'Color', dataIndex: 'Color', key: 'Color' },
    { title: 'Type', dataIndex: 'Type', key: 'Type' },
    {
      title: "Price",
      dataIndex: "Price",
      key: "Price",
      render: renderEditableCell("Price")
    },
    {
      title: "Sell Price",
      dataIndex: "SellPrice",
      key: "SellPrice",
      render: renderEditableCell("SellPrice")
    },
    { title: 'In Stock', dataIndex: 'Stock', key: 'Stock' },
    { title: 'Stock at Laundry', dataIndex: 'StockAtLaundry', key: 'StockAtLaundry' },
    { title: 'Stock at Customers', dataIndex: 'StockAtCustomers', key: 'StockAtCustomers' },
    actionColumn
  ];

  return (
    <>
      <Table
        scroll={{ x: "max-content" }}
        loading={loading}
        className="redesign-table order-by-professional-table"
        dataSource={tableData}
        columns={RoamingItemsFilterColumns({
          columns: columns,
          searchValues,
          handleFilterChange,
        })}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: pagination.total || 10,
          showSizeChanger: true,
          pageSizeOptions: ["10", "50", "100"],
          onChange: handlePageChange,
        }}
      />
      {openForm.editForm && selectedRow &&
        <RoamingItemListEdit
          isModalOpen={openForm.editForm} setIsModalOpen={setOpenForm} selectedRow={selectedRow} />
      }
      {openForm.addToStockForm && selectedRow &&
        <AddToStock
          isModalOpen={openForm.addToStockForm} setIsModalOpen={setOpenForm} selectedRow={selectedRow} />
      }
    </>
  );
};

export default RoamingItemsTable;